import React, { useCallback, useRef, useEffect, useState } from 'react';
import { MdLiveTv } from 'react-icons/md';
import { FiYoutube } from 'react-icons/fi';
import { MdOndemandVideo } from 'react-icons/md';
import { BiRadio } from 'react-icons/bi';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { useHistory, useLocation } from 'react-router-dom';

import api from '../../../services/api';

import { useToast } from '../../../hooks/toast';

import getValidationErrors from '../../../utils/getValidationErrors';

import Input from '../../../components/Input';
import Button from '../../../components/Button';

import { Content } from './styles';
import StandardContainer from '../../../components/StandardContainer';
import Header from '../../../components/Header';
import Select from '../../../components/Select';
import { StatusEnum } from '../../../utils/StatusEnum';
import { PriorityEnum } from '../../../utils/PriorityEnum';
import Checkbox from '../../../components/Checkbox';
import { MediaTypeEnum } from '../../../utils/MediaTypeEnum';

interface MultimediaFormData {
  name: string;
  android_url: string;
  android_priority: string;
  ios_url: string;
  ios_priority: string;
  youtube_url: string;
  youtube_priority: string;
  media_type: string;
  status: string;
  radio_url: string;
  autoplay: string;
}

interface MultimediaData {
  name: string;
  android_url: string;
  android_priority: string;
  ios_url: string;
  ios_priority: string;
  youtube_url: string;
  youtube_priority: string;
  media_type: string;
  active: string;
  is_default: boolean;
  radio_url: string;
  autoplay: string;
}

interface LocationState {
  id: string;
};

const statusOptions = [
  { value: StatusEnum.INATIVO, label: 'Inativo' },
  { value: StatusEnum.ATIVO, label: 'Ativo' },
];

const priorityOptions = [
  { value: PriorityEnum.ALTA, label: 'Alta' },
  { value: PriorityEnum.MEDIA, label: 'Média' },
  { value: PriorityEnum.BAIXA, label: 'Baixa' },
];

const mediaTypeOptions = [
  { value: MediaTypeEnum.RADIO.toString(), label: 'Rádio' },
  { value: MediaTypeEnum.TV.toString(), label: 'TV' },
  { value: MediaTypeEnum.RADIO_TV.toString(), label: 'Rádio e TV' },
];

const EditMultimedia = () => {
  const [multimedia, setMultimedia] = useState<MultimediaData>();
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [checkedAutoPlay, setCheckedAutoPlay] = useState(false);
  const [enableVideo, setEnableVideo] = useState(false);

  const formRef = useRef<FormHandles>(null);
  const { addToast } = useToast();
  const history = useHistory();

  const { state } = useLocation<LocationState>();

  useEffect(() => {
    api.get(`/stream_multimedia/${state.id}`).then(response => {
      setMultimedia(response.data);
      setChecked(response.data.is_default);
      response.data.autoplay === 'Y' ? setCheckedAutoPlay(true) : setCheckedAutoPlay(false);
      setEnableVideo(response.data.enable_video);
    });

  }, [state.id]);

  const handleSubmit = useCallback(
    async (data: MultimediaFormData) => {
      setLoading(true);

      console.log(checkedAutoPlay, 'estado checkedautoplay')

      if (checkedAutoPlay) {
        data.autoplay = 'Y';
      } else {
        data.autoplay = 'N';
      }
      
      try {
        formRef.current?.setErrors({});
        const schema = Yup.object().shape({
          name: Yup.string().required('Nome da transmissão é obrigatório'),
          android_url: Yup.string(),
          android_priority: Yup.string()
            .when('android_url', {
              is: val => !!val.length,
              then: Yup.string().required('Prioridade para m3u8 é obrigatória'),
              otherwise: Yup.string(),
            }),
          ios_url: Yup.string(),
          ios_priority: Yup.string()
            .when('ios_url', {
              is: val => !!val.length,
              then: Yup.string().required('Prioridade para JMV é obrigatória'),
              otherwise: Yup.string(),
            }),
          youtube_url: Yup.string(),
          youtube_priority: Yup.string()
            .when('youtube_url', {
              is: val => !!val.length,
              then: Yup.string().required('Prioridade para Youtube é obrigatória'),
              otherwise: Yup.string(),
            }),
          media_type: Yup.string().required('Tipo de mídia é obrigatório'),
          status: Yup.string().required('Status da transmissão é obrigatório'),
          radio_url: Yup.string(),
          autoplay: Yup.string()
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const status = data.status === 'true' ? true : false;

        const {
          name,
          android_url,
          android_priority,
          ios_url,
          ios_priority,
          youtube_url,
          youtube_priority,
          media_type,
          radio_url,
          autoplay
        } = data;

        let mediaType;

        switch (media_type) {
          case MediaTypeEnum.RADIO.toString():
            mediaType = 1;
            break;
          case MediaTypeEnum.TV.toString():
            mediaType = 2;
            break;
          case MediaTypeEnum.RADIO_TV.toString():
            mediaType = 3;
            break;
          default:
            mediaType = null;
            break;
        }

        await api.put(`/stream_multimedia/${state.id}`, {
          name,
          android_url,
          android_priority: android_url ? Number(android_priority) : null,
          ios_url,
          ios_priority: ios_url ? Number(ios_priority) : null,
          youtube_url,
          youtube_priority: youtube_url ? Number(youtube_priority) : null,
          media_type: mediaType,
          active: status,
          is_default: checked,
          enable_video: enableVideo,
          radio_url,
          autoplay
        });

        history.push('/multimedia');

        addToast({
          type: 'success',
          title: 'Transmissão ao vivo atualizada!',
        });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);
        }

        addToast({
          type: 'error',
          title: 'Erro ao atualizar transmissão',
          description: 'Ocorreu um erro ao atualizar transmissão, tente novamente.',
        });
      }
      setLoading(false);
    },
    [addToast, history, state.id, checked, enableVideo, checkedAutoPlay],
  );

  return (
    <StandardContainer>
      <Header />
      <Content>
        <h1>Editar transmissão ao vivo</h1>
        <Form
          ref={formRef}
          initialData={{
            status: multimedia?.active,
            name: multimedia?.name,
            android_url: multimedia?.android_url,
            android_priority: multimedia?.android_priority,
            ios_url: multimedia?.ios_url,
            ios_priority: multimedia?.ios_priority,
            youtube_url: multimedia?.youtube_url,
            youtube_priority: multimedia?.youtube_priority,
            media_type: multimedia?.media_type?.toString(),
            radio_url: multimedia?.radio_url,
            autoplay: multimedia?.autoplay
          }}
          onSubmit={handleSubmit}
        >
          <div>
            <Select
              name="status"
              defaultOption={multimedia?.active}
              options={statusOptions}
              label="Selecione o status da transmissão"
              style={{ marginBottom: '7px' }}
            />
            <Input name="name" icon={MdLiveTv} label="Nome da transmissão" placeholder="Nome da transmissão" />
            <Select
              name="android_priority"
              defaultOption={multimedia?.android_priority}
              options={priorityOptions}
              label="Selecione a prioridade para o m3u8"
              style={{ marginBottom: '7px' }}
            />
            <Input name="android_url" icon={MdOndemandVideo} label="Link para m3u8" placeholder="Link para m3u8" />

            <Select
              name="ios_priority"
              defaultOption={multimedia?.ios_priority}
              options={priorityOptions}
              label="Selecione a prioridade para o JMV"
              style={{ marginBottom: '7px' }}
            />
            <Input name="ios_url" icon={MdOndemandVideo} label="Link para JMV" placeholder="Link para JMV" />
          </div>
          <div>
            <Select
              name="youtube_priority"
              defaultOption={multimedia?.youtube_priority}
              options={priorityOptions}
              label="Selecione a prioridade para o Youtube"
              style={{ marginBottom: '7px' }}
            />
            <Input name="youtube_url" icon={FiYoutube} label="Link para youtube" placeholder="Link para youtube" />
            <Input name="radio_url" icon={BiRadio} label="Link para rádio" placeholder="Link para rádio" />
            <Select
              name="media_type"
              defaultOption={multimedia?.media_type?.toString()}
              options={mediaTypeOptions}
              label="Selecione o tipo de mídia"
              style={{ marginBottom: '7px' }}
            />

            <Checkbox
              name="checkbox"
              checkboxName="Transmissão padrão"
              checked={checked}
              onChecked={() => setChecked(!checked)}
            />
            <Checkbox
              name="checkbox_enable_video"
              checkboxName="Vídeo habilitado"
              checked={enableVideo}
              onChecked={() => setEnableVideo(!enableVideo)}
            />
              <Checkbox
              name="checkbox_auto_play"
              checkboxName="AutoPlay habilitado"
              checked={checkedAutoPlay}
                     onChecked={() => {
                const newCheckedAutoPlay = !checkedAutoPlay;
                console.log(
                  'Novo estado de checkedAutoPlay:',
                  newCheckedAutoPlay,
                );
                setCheckedAutoPlay(newCheckedAutoPlay);
              }}
              // onChecked={() => setCheckedAutoPlay(!checkedAutoPlay)}
            />
          </div>
          <Button type="submit" loading={loading}>Salvar</Button>
        </Form>
      </Content>
    </StandardContainer>
  )
}

export default EditMultimedia;
